import { useEffect, useMemo, useRef, useState } from "react";
import type { HTMLInputTypeAttribute } from "react";
import { ChevronDownIcon } from "lucide-react";
import { toast } from "sonner";

import DateTimePicker from "../../components/DateTimePicker";
import ConfirmMessageModal from "components/ConfirmMessageModal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

import {
  getStampVendors,
  type StampVendorOption,
} from "../../api/discussion";
import {
  FollowUpFilingStep,
  getFollowUpStampPaperDetails,
  getFollowUpStampPaperPreview,
  sendFollowUpStampPaperWhatsApp,
  updateFollowUpStampPaperDetails,
  type FollowUpStampPaperApplication,
  type FollowUpStampPaperStepData,
  type UpdateFollowUpStampPaperDetailsPayload,
} from "../../api/followUpFiling";
import { getFollowUpProgress } from "../../api/followUpDiscussion";
import { normalizeApiDateToIsoForForm } from "../../utils/filingApiDates";

export const FOLLOW_UP_STAMP_PAPER_DETAILS_TO_VENDOR_FORM_ID =
  "follow-up-stamp-paper-details-to-vendor-form";

const APPLICANT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "SOLE_PROPRIETORSHIP", label: "Sole Proprietorship" },
  { value: "TRUST", label: "Trust" },
  { value: "PARTNERSHIP_FIRM", label: "Partnership Firm" },
  { value: "SOCIETY", label: "Society" },
  { value: "PRIVATE_LIMITED_COMPANY", label: "Private Limited Company" },
  {
    value: "LIMITED_LIABILITY_PARTNERSHIP",
    label: "Limited Liability Partnership",
  },
  { value: "OTHERS", label: "Others" },
];

const STAMP_PAPER_COUNT_OPTIONS = Array.from({ length: 11 }, (_, i) => ({
  value: String(i),
  label: String(i),
}));

const USER_DATE_PROPOSED = "PROPOSED_TO_BE_USED";

const LABEL_STYLE = "text-xs font-normal leading-5 flex items-center";
const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;
const INPUT_BASE =
  "box-border flex flex-row items-center justify-between gap-2.5 px-[10px] py-2 h-9 min-h-[44px] md:min-h-[36px] w-full bg-white border border-[#EEEEEE] rounded-[6px] focus-within:ring-1 focus-within:ring-[#222]";
const PLACEHOLDER_CLASS =
  "text-[12px] md:text-xs font-normal leading-5 text-[#6D6D6D]";
const TEXT_CLASS =
  "text-[12px] md:text-xs font-normal leading-5 text-[#222222]";

type StampVendorFieldErrors = {
  stampVendorName?: string;
  stampVendorPhone?: string;
};

function validateVendorFields(
  name: string,
  phone: string
): { ok: true } | { ok: false; errors: StampVendorFieldErrors } {
  const errors: StampVendorFieldErrors = {};
  const trimmedName = name.trim();
  const trimmedPhone = phone.trim();
  if (!trimmedName) {
    errors.stampVendorName = "Enter the stamp vendor name";
  }
  if (!trimmedPhone) {
    errors.stampVendorPhone = "Enter the stamp vendor phone number";
  } else {
    const digits = trimmedPhone.replace(/\D/g, "");
    if (digits.length < 8 || digits.length > 15) {
      errors.stampVendorPhone = "Enter a valid phone number";
    }
  }
  if (errors.stampVendorName || errors.stampVendorPhone) {
    return { ok: false, errors };
  }
  return { ok: true };
}

function formatApplicantTypeLabel(value: string | null | undefined): string {
  if (!value) return "";
  const found = APPLICANT_TYPE_OPTIONS.find((o) => o.value === value);
  return found?.label ?? value;
}

function TextField({
  label,
  value,
  onChange,
  placeholder = "Text Here",
  labelMuted = false,
  readOnly = false,
  type = "text",
  inputMode,
  errorText,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  labelMuted?: boolean;
  readOnly?: boolean;
  type?: HTMLInputTypeAttribute;
  inputMode?:
    | "none"
    | "text"
    | "tel"
    | "url"
    | "email"
    | "numeric"
    | "decimal"
    | "search";
  errorText?: string;
}) {
  return (
    <div
      className="flex flex-1 min-w-0 flex-col gap-1 w-full"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <label
        className={LABEL_STYLE}
        style={{
          ...LABEL_FEATURES,
          color: labelMuted ? "#B0B0B0" : "#222222",
        }}
      >
        {label}
      </label>
      <div
        className={`${INPUT_BASE} ${
          errorText ? "border-red-400 focus-within:ring-red-400" : ""
        }`}
      >
        <input
          type={type}
          inputMode={inputMode}
          value={value}
          onChange={(e) => !readOnly && onChange(e.target.value)}
          placeholder={placeholder}
          readOnly={readOnly}
          className={`min-w-0 flex-1 bg-transparent border-0 focus:outline-none focus:ring-0 ${
            value ? TEXT_CLASS : PLACEHOLDER_CLASS
          } ${readOnly ? "cursor-default" : ""}`}
          style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
        />
      </div>
      {errorText ? (
        <p className="text-[11px] leading-4 text-red-600" role="alert">
          {errorText}
        </p>
      ) : null}
    </div>
  );
}

type FollowUpStampPaperDetailsToVendorViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSentStatusChange?: (sent: boolean) => void;
};

export default function FollowUpStampPaperDetailsToVendorView({
  leadId,
  onSuccess,
  onSentStatusChange,
}: FollowUpStampPaperDetailsToVendorViewProps) {
  const [stampVendorName, setStampVendorName] = useState("");
  const [stampVendorPhone, setStampVendorPhone] = useState("");
  const [stampVendorFieldErrors, setStampVendorFieldErrors] =
    useState<StampVendorFieldErrors>({});

  const [fetched, setFetched] =
    useState<FollowUpStampPaperStepData["fetched"]>({
      applicant: null,
      applicantAddress: "",
      applicantType: null,
      signatoryName: null,
      signatoryDesignation: null,
    });
  const [applications, setApplications] = useState<
    FollowUpStampPaperApplication[]
  >([]);

  const [stampVendors, setStampVendors] = useState<StampVendorOption[]>([]);
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);

  const [previewMessage, setPreviewMessage] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [hasSentWhatsapp, setHasSentWhatsapp] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");

  const previewDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoSaveDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const totalStampPapersForPoa = useMemo(
    () =>
      applications.reduce(
        (sum, app) => sum + (Number(app.numberOfStampPapersForPoa) || 0),
        0
      ),
    [applications]
  );

  const applyStepData = (data: FollowUpStampPaperStepData) => {
    setFetched(data.fetched);
    setStampVendorName(data.stampVendorName ?? "");
    setStampVendorPhone(data.stampVendorPhone ?? "");
    setApplications(
      (data.applications ?? []).map((app) => ({
        ...app,
        date: app.date
          ? normalizeApiDateToIsoForForm(app.date) ?? app.date
          : null,
        numberOfStampPapersForPoa: Number(app.numberOfStampPapersForPoa) || 0,
      }))
    );
  };

  // Initial load: step data + saved stamp vendors list
  useEffect(() => {
    if (!leadId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getFollowUpStampPaperDetails(leadId),
      getStampVendors().catch((): StampVendorOption[] => []),
    ])
      .then(([data, vendors]) => {
        if (cancelled) return;
        applyStepData(data);
        if (Array.isArray(vendors) && vendors.length > 0) {
          setStampVendors(vendors);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err instanceof Error ? err.message : "Failed to load stamp paper details"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  // Workflow progress – is this step already completed (sent)?
  useEffect(() => {
    if (!leadId) {
      setHasSentWhatsapp(false);
      onSentStatusChange?.(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const progress = await getFollowUpProgress(leadId);
        const step = progress.steps.find(
          (s) => s.slug === FollowUpFilingStep.FU_S2_01_StampPaperDetailsToVendor
        );
        if (!cancelled && step) {
          setHasSentWhatsapp(step.isCompleted);
          onSentStatusChange?.(step.isCompleted);
        }
      } catch {
        if (!cancelled) {
          setHasSentWhatsapp(false);
          onSentStatusChange?.(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, onSentStatusChange]);

  // Initial preview load
  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    getFollowUpStampPaperPreview(leadId)
      .then((res) => {
        if (!cancelled) setPreviewMessage(res.message ?? "");
      })
      .catch(() => {
        if (!cancelled) setPreviewMessage("");
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  // Debounced preview refresh whenever the preview-relevant fields change.
  useEffect(() => {
    if (!leadId) return;
    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    previewDebounceRef.current = setTimeout(() => {
      getFollowUpStampPaperPreview(leadId)
        .then((res) => setPreviewMessage(res.message ?? ""))
        .catch(() => {});
    }, 350);
    return () => {
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    };
  }, [leadId, totalStampPapersForPoa]);

  const handleVendorSelect = (value: string) => {
    if (value === "__none__") return;
    setStampVendorName(value);
    const vendor = stampVendors.find((v) => v.name === value);
    setStampVendorPhone(vendor?.phone ?? "");
    setStampVendorFieldErrors({});
  };

  const vendorSelectValue =
    stampVendorName && stampVendors.some((v) => v.name === stampVendorName)
      ? stampVendorName
      : "__none__";

  const buildPayload = (): UpdateFollowUpStampPaperDetailsPayload => ({
    stampPapersPerApplication: applications.map((app) => ({
      applicationId: app.applicationId,
      tmNo: app.tmNo,
      numberOfStampPapersForPoa: Number(app.numberOfStampPapersForPoa) || 0,
    })),
    stampVendorName: stampVendorName.trim() || undefined,
    stampVendorPhone: stampVendorPhone.trim() || undefined,
  });

  // Auto-save (debounced) when per-application stamp paper counts change.
  // Only triggers once initial step data is loaded and at least one application row exists.
  useEffect(() => {
    if (!leadId || loading || applications.length === 0) return;
    if (autoSaveDebounceRef.current) clearTimeout(autoSaveDebounceRef.current);
    autoSaveDebounceRef.current = setTimeout(() => {
      updateFollowUpStampPaperDetails(leadId, buildPayload())
        .then((data) => {
          // Refresh preview after persistence so the WhatsApp text reflects the latest values.
          getFollowUpStampPaperPreview(leadId)
            .then((res) => setPreviewMessage(res.message ?? ""))
            .catch(() => {});
          // Apply any backend normalisation silently.
          applyStepData(data);
        })
        .catch(() => {
          // Auto-save errors are non-blocking; the explicit submit will surface any real issue.
        });
    }, 600);
    return () => {
      if (autoSaveDebounceRef.current)
        clearTimeout(autoSaveDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, totalStampPapersForPoa]);

  const updateApplicationCount = (index: number, value: string) => {
    setApplications((prev) => {
      const next = [...prev];
      const parsed = parseInt(value, 10);
      next[index] = {
        ...next[index],
        numberOfStampPapersForPoa: Number.isFinite(parsed) ? parsed : 0,
      };
      return next;
    });
  };

  const persistAndRefresh = async () => {
    if (!leadId) return null;
    const data = await updateFollowUpStampPaperDetails(leadId, buildPayload());
    applyStepData(data);
    const preview = await getFollowUpStampPaperPreview(leadId).catch(() => ({
      message: "",
      canSend: false,
    }));
    setPreviewMessage(preview.message ?? "");
    return data;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;
    const validation = validateVendorFields(stampVendorName, stampVendorPhone);
    if ("errors" in validation) {
      setStampVendorFieldErrors(validation.errors);
      const first =
        validation.errors.stampVendorName ?? validation.errors.stampVendorPhone;
      if (first) toast.error(first);
      return;
    }
    if (totalStampPapersForPoa < 1) {
      toast.error(
        "Number of Stamp Papers for PoA must be at least 1 across applications."
      );
      return;
    }
    setStampVendorFieldErrors({});
    setSaving(true);
    try {
      await persistAndRefresh();
      if (hasSentWhatsapp) {
        toast.success("Stamp paper details to vendor saved successfully.");
        onSuccess?.(leadId);
      } else {
        setConfirmMode("send");
        setShowConfirmModal(true);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmProceed = () => {
    if (!leadId) return;
    setShowConfirmModal(false);
    (async () => {
      try {
        await sendFollowUpStampPaperWhatsApp(leadId);
        setHasSentWhatsapp(true);
        onSentStatusChange?.(true);
        toast.success(
          confirmMode === "resend"
            ? "Stamp paper details resent to vendor via WhatsApp."
            : "Stamp paper details sent to vendor successfully."
        );
        onSuccess?.(leadId);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to send");
      }
    })();
  };

  const handleResend = async () => {
    if (!leadId || resending || saving) return;
    const validation = validateVendorFields(stampVendorName, stampVendorPhone);
    if ("errors" in validation) {
      setStampVendorFieldErrors(validation.errors);
      const first =
        validation.errors.stampVendorName ?? validation.errors.stampVendorPhone;
      if (first) toast.error(first);
      return;
    }
    if (totalStampPapersForPoa < 1) {
      toast.error(
        "Number of Stamp Papers for PoA must be at least 1 across applications."
      );
      return;
    }
    setStampVendorFieldErrors({});
    setResending(true);
    try {
      await persistAndRefresh();
      setConfirmMode("resend");
      setShowConfirmModal(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to resend");
    } finally {
      setResending(false);
    }
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this form.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading stamp paper details...</p>
      </div>
    );
  }

  return (
    <form
      id={FOLLOW_UP_STAMP_PAPER_DETAILS_TO_VENDOR_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 md:gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <h3 className="text-sm font-medium text-[#222222]">
        Stamp Paper details to Vendor
      </h3>

      {/* Quick select vendor (autofill) */}
      <div className="flex flex-col w-full gap-1">
        <label
          className={LABEL_STYLE}
          style={{ ...LABEL_FEATURES, color: "#222222" }}
        >
          Name of Stamp Vendor
        </label>
        <Select value={vendorSelectValue} onValueChange={handleVendorSelect}>
          <SelectTrigger className="h-9 min-h-[44px] md:min-h-[36px] w-full border border-[#eee] rounded-[6px] px-2.5 py-2 text-[12px] md:text-xs text-[#222] data-[placeholder]:text-[#6D6D6D]">
            <SelectValue placeholder="Select Vendor" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— Not from list —</SelectItem>
            {stampVendors
              .filter(
                (v) => typeof v.name === "string" && v.name.trim().length > 0
              )
              .map((v) => (
                <SelectItem key={v.name} value={v.name}>
                  {v.name}
                  {v.isDefault ? " (default)" : ""}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      {/* Vendor Name | Vendor Phone Number */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5 w-full">
        <TextField
          label="Vendor Name"
          value={stampVendorName}
          onChange={(v) => {
            setStampVendorName(v);
            setStampVendorFieldErrors((prev) => ({
              ...prev,
              stampVendorName: undefined,
            }));
          }}
          placeholder="Text Here"
          errorText={stampVendorFieldErrors.stampVendorName}
        />
        <TextField
          label="Vendor Phone Number"
          value={stampVendorPhone}
          onChange={(v) => {
            setStampVendorPhone(v);
            setStampVendorFieldErrors((prev) => ({
              ...prev,
              stampVendorPhone: undefined,
            }));
          }}
          placeholder="Text Here"
          type="tel"
          inputMode="tel"
          errorText={stampVendorFieldErrors.stampVendorPhone}
        />
      </div>

      {/* Applicant | Applicant Address (read-only fetched data) */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5 w-full">
        <TextField
          label="Applicant"
          value={fetched.applicant ?? ""}
          onChange={() => {}}
          placeholder="Text Here"
          readOnly
        />
        <TextField
          label="Applicant Address"
          value={fetched.applicantAddress ?? ""}
          onChange={() => {}}
          placeholder="Text Here"
          readOnly
        />
      </div>

      {/* Applicant Type | Signatory's Name */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5 w-full">
        <TextField
          label="Applicant Type"
          value={formatApplicantTypeLabel(fetched.applicantType)}
          onChange={() => {}}
          placeholder="Text Here"
          readOnly
        />
        <TextField
          label="Signatory's Name"
          value={fetched.signatoryName ?? ""}
          onChange={() => {}}
          placeholder="Text Here"
          readOnly
        />
      </div>

      {/* Signatory's Designation | Number of Stamp Papers for PoA (total, read-only) */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-4 md:gap-5 w-full">
        <TextField
          label="Signatory's Designation"
          value={fetched.signatoryDesignation ?? ""}
          onChange={() => {}}
          placeholder="Text Here"
          readOnly
        />
        <TextField
          label="Number of Stamp Papers for PoA"
          value={String(totalStampPapersForPoa)}
          onChange={() => {}}
          placeholder="0"
          readOnly
        />
      </div>

      {/* Per-application table: Class | Type Mark | User Date | Number of Stamp Papers for PoA */}
      {applications.length > 0 && (
        <div
          className="flex flex-col w-full"
          style={{
            fontFamily: "Inter, sans-serif",
            fontFeatureSettings: "'ss01' on, 'cv01' on",
          }}
        >
          <div className="hidden lg:grid lg:grid-cols-[160px_230px_220px_1fr] gap-5 items-center shrink-0 text-[#222] text-sm font-medium leading-5 mb-4">
            <div>Classes</div>
            <div>Type Mark</div>
            <div>User Date</div>
            <div className="whitespace-nowrap">Number of Stamp Papers for PoA</div>
          </div>
          <div className="flex flex-col gap-3">
            {applications.map((app, idx) => (
              <div
                key={app.applicationId}
                className="grid grid-cols-1 lg:grid-cols-[160px_230px_220px_1fr] gap-3 lg:gap-5 lg:items-center w-full p-4 lg:p-0 rounded-[6px] border border-[#EEE] lg:border-0 bg-white lg:bg-transparent"
              >
                {/* Class (read-only chip) */}
                <div className="flex flex-col gap-1">
                  <span className="lg:hidden text-xs font-normal text-[#222222]">
                    Classes
                  </span>
                  <div className="flex w-full items-center justify-between gap-2 min-h-[44px] lg:min-h-0 bg-[#f7f7f7] border border-[#eee] rounded-[6px] p-2.5">
                    <span className="text-[12px] lg:text-sm text-[#222] whitespace-nowrap">
                      {app.classNumber != null
                        ? `Class ${app.classNumber}`
                        : "—"}
                    </span>
                    <ChevronDownIcon className="w-4 h-4 shrink-0 text-[#6D6D6D]" />
                  </div>
                </div>

                {/* Type of Mark (read-only) */}
                <div className="flex flex-col gap-1 w-full">
                  <span className="lg:hidden text-xs font-normal text-[#222222]">
                    Type Mark
                  </span>
                  <div className="flex items-center h-9 min-h-[44px] lg:min-h-[36px] w-full border border-[#eee] rounded-[6px] px-2.5 py-2 text-[12px] lg:text-xs bg-white">
                    <span
                      className={
                        app.typeOfMark ? "text-[#222]" : "text-[#6D6D6D]"
                      }
                    >
                      {app.typeOfMark || "—"}
                    </span>
                  </div>
                </div>

                {/* User Date (read-only) */}
                <div className="flex flex-col gap-1 w-full">
                  <span className="lg:hidden text-xs font-normal text-[#222222]">
                    User Date
                  </span>
                  <div className="w-full">
                    {app.userDateType === USER_DATE_PROPOSED ? (
                      <div className="flex items-center h-9 min-h-[44px] lg:min-h-[36px] w-full border border-[#eee] rounded-[6px] px-2.5 py-2 bg-white text-[12px] lg:text-xs text-[#222]">
                        Proposed to be used
                      </div>
                    ) : (
                      <DateTimePicker
                        value={app.date ? new Date(app.date) : null}
                        onChange={() => {}}
                        showTime={false}
                        readOnly
                        placeholder="Select Date"
                        buttonClassName="min-h-[44px] lg:min-h-0 text-[12px] lg:text-xs"
                      />
                    )}
                  </div>
                </div>

                {/* Number of Stamp Papers for PoA (editable per application) */}
                <div className="flex flex-col gap-1 w-full">
                  <span className="lg:hidden text-xs font-normal text-[#222222]">
                    Number of Stamp Papers for PoA
                  </span>
                  <Select
                    value={String(app.numberOfStampPapersForPoa ?? 0)}
                    onValueChange={(v) => updateApplicationCount(idx, v)}
                  >
                    <SelectTrigger className="h-9 min-h-[44px] lg:min-h-[36px] w-full border border-[#eee] rounded-[6px] px-2.5 py-2 text-[12px] lg:text-xs text-[#222] data-[placeholder]:text-[#6D6D6D]">
                      <SelectValue placeholder="Select an option" />
                    </SelectTrigger>
                    <SelectContent>
                      {STAMP_PAPER_COUNT_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ConfirmMessageModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        previewContent={previewMessage || "—"}
        onConfirm={handleConfirmProceed}
        disableConfirm={!previewMessage?.trim()}
      />

      {hasSentWhatsapp && leadId && (
        <div className="flex items-center gap-3 mt-2">
          <button
            type="button"
            disabled={resending || saving}
            onClick={handleResend}
            className="inline-flex items-center justify-center h-9 px-4 text-xs font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {resending ? "Resending…" : "Resend Message"}
          </button>
        </div>
      )}
    </form>
  );
}
